import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrder } from '../../context/OrderContext';
import './TranslitionOrder.css';

const TranslationOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { orderState, setOrderData, setCurrentStep } = useOrder();
  
  const [quoteDetails, setQuoteDetails] = useState({
    wordCount: 0,
    pricePerWord: 0,
    totalPrice: 0,
    deliveryTime: 0,
    currency: '',
    originalFilename: '',
    quoteId: '',
    sourceLanguage: '',
    targetLanguage: '',
    loading: true,
    error: null,
    needsManualReview: false,
    reviewMessage: '',
  });
  

  // Process the quote data when component mounts
  useEffect(() => {
    // Check OrderContext first
    if (orderState.quoteDetails) {
      const quote = orderState.quoteDetails;
      
      setQuoteDetails({
        wordCount: quote.word_count,
        pricePerWord: quote.price_per_250_words,
        totalPrice: quote.total_price,
        deliveryTime: quote.translation_type.average_delivery_time,
        currency: quote.currency,
        originalFilename: quote.original_filename,
        quoteId: quote.quote_id,
        sourceLanguage: quote.source_language.name,
        targetLanguage: quote.target_language.name,
        loading: false,
        error: null,
        needsManualReview: quote.needs_manual_review || false,
        reviewMessage: quote.review_message || '',
      });
      

    } 
    // Fall back to location state if OrderContext is empty (direct navigation with state)
    else if (location.state && location.state.quoteDetails) {
      const quote = location.state.quoteDetails;
      
      setQuoteDetails({
        wordCount: quote.word_count,
        pricePerWord: quote.price_per_250_words,
        totalPrice: quote.total_price,
        deliveryTime: quote.translation_type.average_delivery_time,
        currency: quote.currency,
        originalFilename: quote.original_filename,
        quoteId: quote.quote_id,
        sourceLanguage: quote.source_language.name,
        targetLanguage: quote.target_language.name,
        loading: false,
        error: null,
        needsManualReview: quote.needs_manual_review || false,
        reviewMessage: quote.review_message || '',
      });
      

    } else {
      // No quote data found
      setQuoteDetails(prevState => ({
        ...prevState,
        loading: false,
        error: t('translation_order.error_no_data')
      }));
    }
  }, [location.state, orderState.quoteDetails, t]);
  
  const handleProceedToPayment = () => {

    
    // Store order data in context
    setOrderData({
      quoteId: quoteDetails.quoteId,
      totalPrice: quoteDetails.totalPrice,
      currency: quoteDetails.currency
    });
    
    // Set current step to 2 (Translation summary completed)
    setCurrentStep(2);
    
    // Navigate to payment page with the quote data
    navigate(`/payment1`, {
      state: { 
        quoteId: quoteDetails.quoteId,
        quoteDetails: quoteDetails 
      }
    });
  };
  
  const handleCancel = () => {
    navigate('/services');
  };
  

  
  const handleWhatsAppRedirect = () => {
    // WhatsApp number should be configured properly
    const whatsappNumber = "+96896758894"; // Replace with actual WhatsApp number
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(t('translation_order.whatsapp_message'))}`;
    window.open(whatsappUrl, '_blank');
  };
  
  // Show loading state
  if (quoteDetails.loading) {
    return (
      <div className="services-page">
        <div className="container">
          <h1 className="services-title">{t('translation_order.title')}</h1>
          <div className="loading">{t('translation_order.loading')}</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (quoteDetails.error) {
    return (
      <div className="services-page">
        <div className="container">
          <h1 className="services-title">{t('translation_order.title')}</h1>
          <div className="error">{quoteDetails.error}</div>
          <button className="cancel-btn" onClick={handleCancel}>
            {t('translation_order.back')}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="services-page">
      <div className="container">
        <h1 className="services-title">{t('translation_order.title')}</h1>
        <p className="services-subtitle">{t('translation_order.subtitle')}</p>
        
        <div className="services-container">
          <div className="order-summary-content">
            <div className="order-summary-icon">
              <svg width="120" height="120" viewBox="0 0 80 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 15H24V45H50V15Z" fill="white"/>
                <path d="M50 15H24V45H50V15Z" stroke="#333333" strokeWidth="2"/>
                <path d="M30 25H44" stroke="#333333" strokeWidth="2"/>
                <path d="M30 30H44" stroke="#333333" strokeWidth="2"/>
                <path d="M30 35H38" stroke="#333333" strokeWidth="2"/>
                <path d="M55 45H24C20.6863 45 18 47.6863 18 51V55H49C52.3137 55 55 52.3137 55 49V45Z" fill="#0A4B66"/>
              </svg>
            </div>
            
            <div className="order-summary-details">
              <div className="order-detail-row">
                <div className="order-detail-label">{t('translation_order.filename')}:</div>
                <div className="order-detail-value">{quoteDetails.originalFilename}</div>
              </div>

              <div className="order-detail-row">
                <div className="order-detail-label">{t('translation_order.from_language')}:</div>
                <div className="order-detail-value">{quoteDetails.sourceLanguage}</div>
              </div>

              <div className="order-detail-row">
                <div className="order-detail-label">{t('translation_order.to_language')}:</div>
                <div className="order-detail-value">{quoteDetails.targetLanguage}</div>
              </div>
              
              <div className="order-detail-row">
                <div className="order-detail-label">{t('translation_order.word_count')}:</div>
                <div className="order-detail-value">{quoteDetails.wordCount}</div>
              </div>
              
              <div className="order-detail-row">
                <div className="order-detail-label">{t('translation_order.price_per_250_words')}:</div>
                <div className="order-detail-value">{quoteDetails.pricePerWord} {quoteDetails.currency}</div>
              </div>
              
              <div className="order-detail-row">
                <div className="order-detail-label">{t('translation_order.total')}:</div>
                <div className="order-detail-value">{quoteDetails.totalPrice} {quoteDetails.currency}</div>
              </div>
              
              <div className="order-detail-row">
                <div className="order-detail-label">{t('translation_order.delivery_time')}:</div>
                <div className="order-detail-value">{t('translation_order.within_hours', { hours: quoteDetails.deliveryTime })}</div>
              </div>
            </div>
          </div>
          
          <div className="order-actions">
            <button className="cancel-btn" onClick={handleCancel}>
              {t('translation_order.cancel_order')}
            </button>
            <button className="proceed-btn" onClick={handleProceedToPayment}>
              {t('translation_order.proceed_to_payment')}
            </button>
          </div>
        </div>
      </div>
      
      
    </div>
  );
};

export default TranslationOrder;